import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import {
  ContactReferenceModel,
  MerchantAccount,
  PaymentMethodModel,
  UpsertInvoiceModel,
} from "../models";
import {
  AuthService,
  ContactService,
  InvoiceService,
  MerchantAccountService,
  PaymentMethodService,
} from "../services";
import DatePicker from "@mindmemobile/mm-date-picker";
import {
  ApplicationStatus,
  BillingMethodType,
  InvoiceStatus,
  PaymentTransactionStatus,
} from "../enums";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  IAPIResponse,
  Input,
  IOption,
  Loader,
  ModalComponent,
  SapphireBlueButton,
  SingleSelect,
  StatusDropdownWrapper,
  TextInputWithDropdown,
  TimeAMPM,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { InvoiceUtil } from "../utils";
import RenderPaymentMethod from "./RenderPaymentMethod";
import { InvoicePopupWrapper } from "../styles";
import RenderContactInfo from "./RenderContactInfo";
import ContactDropDown from "./ContactDropDown";
import { AccountSettingsStore } from "../store";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import BillingMethods from "./BillingMethods";
import PaymentMethodDropdown from "./PaymentMethodDropdown";
import { IAPICreateUpdatePayInvoiceModel } from "../interfaces";

type IProps = {
  navigate: NavigateFunction;
  onClose: (refresh: boolean, contactId?: string) => void;
  invoiceData: UpsertInvoiceModel;
};

const UpsertInvoicePopUp: React.FC<IProps> = ({ onClose, invoiceData }) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodModel[]>(
    []
  );
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    []
  );
  const [data, setInvoiceData] = React.useState<UpsertInvoiceModel>(
    new UpsertInvoiceModel({
      ...invoiceData,
      isContactRequired:
        !invoiceData.contact?.id && AuthService.isMindMeContactRoute
          ? false
          : true,
    })
  );

  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [contacts, setContactsData] = React.useState<ContactReferenceModel[]>(
    []
  );
  const [showBalanceDetails, toggleBalanceDetails] = React.useState(
    data.isPayPopUp ? false : true
  );
  const [showPayBalanceDetails, togglePayBalanceDetails] = React.useState(true);
  const [showLoader, setShowLoader] = React.useState(true);
  const [hasMoreContacts, setHasMoreContacts] = React.useState(false);

  const redColor = "#FF0000";
  function isUpdate() {
    return data.id !== "";
  }

  useEffect(() => {
    loadInitialData();
  }, []);

  const formatAmount = (value: string): string => {
    if (parseFloat(value) < 0 || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(value.toString().replace(/,/g, "") || "0.00"),
      true
    );
  };

  const loadInitialData = async () => {
    try {
      setShowLoader(true);
      await Promise.all([
        fetchPaymentMethods(),
        fetchMerchantAccounts(),
        fetchContacts(data.contact?.id, data.contact?.mindmeContactId),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setShowLoader(false);
    }
  };

  const fetchPaymentMethods = async () => {
    const response = await PaymentMethodService.getPaymentMethods().toPromise();

    if (response?.result) {
      const paymentMethods = response.result.map((paymentMethod) =>
        PaymentMethodModel.deserializeAPIModel(paymentMethod)
      );
      setPaymentMethods(paymentMethods);

      const selectedPaymentMethod =
        PaymentMethodModel.getDefaultSelecedPaymentMethod(
          paymentMethods,
          data.contact?.id || ""
        );

      setInvoiceData(
        (prevData) =>
          new UpsertInvoiceModel({
            ...prevData,
            paymentMethod: !prevData.paymentMethod
              ? selectedPaymentMethod
              : prevData.paymentMethod,
          })
      );
    }
  };

  const fetchMerchantAccounts = async () => {
    const response = await MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).toPromise();

    if (response?.result) {
      const activeMerchantAccounts = (
        response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
        []
      ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

      const defaultMerchantAccount =
        activeMerchantAccounts?.length > 0
          ? activeMerchantAccounts[0]
          : undefined;

      setInvoiceData(
        (prevData) =>
          new UpsertInvoiceModel({
            ...prevData,
            merchantAccount: !prevData.merchantAccount
              ? defaultMerchantAccount
              : prevData.merchantAccount,
          })
      );
      setMerchantAccounts(activeMerchantAccounts);
    }
  };

  const fetchContacts = async (
    paymentContactId?: string,
    mindmeContactId?: string,
    searchValue?: string,
    pageSize: number = 50
  ) => {
    const response = await ContactService.getMindMeContacts(
      AuthService.authData.accountId,
      paymentContactId,
      mindmeContactId,
      searchValue,
      pageSize
    ).toPromise();

    if (response?.result) {
      const contacts = response.result?.data?.map((contact) =>
        ContactReferenceModel.deserializeFromMindMeContacts(contact)
      );
      setContactsData(contacts);
      setHasMoreContacts(response.result?.hasMoreData || false);
      if (paymentContactId) {
        const defaultPaymentMethod =
          PaymentMethodModel.getDefaultSelecedPaymentMethod(
            paymentMethods,
            paymentContactId
          );
        setInvoiceData(
          (prevData) =>
            new UpsertInvoiceModel({
              ...prevData,
              contact: contacts.find((c) => c.id === paymentContactId),
              paymentMethod: defaultPaymentMethod,
            })
        );
      }
    }
  };

  function row(
    label: string,
    rightSide: React.ReactNode,
    className?: string,
    showLine = false
  ) {
    {
      return (
        <FlexContainer spacing={30} className={className}>
          <CustomLabel>{label}</CustomLabel>
          <div>
            <FlexContainer alignItems="center" spacing={20}>
              {rightSide}
            </FlexContainer>
            {showLine && <div className="balance-line" />}
          </div>
        </FlexContainer>
      );
    }
  }

  function updateState(
    key: keyof UpsertInvoiceModel,
    value:
      | string
      | boolean
      | number
      | Date
      | PaymentMethodModel
      | MerchantAccount,
    isBlur?: boolean
  ) {
    const clonedData = new UpsertInvoiceModel(data);
    switch (key) {
      case "description":
        clonedData.description = value as string;
        break;
      case "amount":
        if (isBlur) {
          value = formatAmount(value as string);
        }
        clonedData.amount = value as string;
        break;
      case "balanceValue":
        clonedData.balanceValue = value as string;
        break;
      case "billingMethod":
        clonedData.billingMethod = value as keyof typeof BillingMethodType;
        break;
      case "dueDate":
        clonedData.dueDate = value as string;
        break;
      case "amountToPay":
        if (isBlur) {
          value = formatAmount(value as string);
        }
        clonedData.amountToPay = value as string;
        break;
      case "isDraft":
        clonedData.isDraft = value as boolean;
        break;
      case "paymentMethod":
        clonedData.paymentMethod = value as PaymentMethodModel;
        break;
      case "merchantAccount":
        clonedData.merchantAccount = value as MerchantAccount;
        break;
      case "status":
        clonedData.status = value as keyof typeof InvoiceStatus;
        break;
      case "isNewPaymentMethodPrimary":
        clonedData.isNewPaymentMethodPrimary = value === true;
        break;
    }
    clonedData.validate(true);
    setInvoiceData(clonedData);
  }

  function updateTime(amPm: keyof typeof TimeAMPM, time: string) {
    const clonedData = new UpsertInvoiceModel(data);
    clonedData.time = time;
    clonedData.amPm = amPm;
    clonedData.validate(true);
    setInvoiceData(clonedData);
  }

  function textInput(
    type: "text" | "textarea" | "number" | "email" | "date",
    key: keyof UpsertInvoiceModel,
    value: string,
    placeHolder: string,
    className: string
  ) {
    return (
      <Input
        type={type}
        value={value}
        error={data.hasError(key)}
        className={className}
        onChange={(e, isBlur) => updateState(key, e.target.value, isBlur)}
        placeholder={placeHolder}
        onDateChange={(date) => updateState(key, date)}
        minDate={isUpdate() ? undefined : new Date()}
      />
    );
  }

  function checkBox(
    label: string,
    value: boolean,
    key: keyof UpsertInvoiceModel
  ) {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => updateState(key, e.target.checked)}
        label={label}
      />
    );
  }

  function onSubmit() {
    const success = data.validate();
    if (!success) {
      setInvoiceData(new UpsertInvoiceModel(data));
      return;
    }
    setAPIRequestPending(true);
    const errorMessage = data.isPayPopUp
      ? "Error paying invoice"
      : data.id
        ? "Error updating invoice"
        : "Error saving invoice";
    const successResposne = (
      response: IAPIResponse<IAPICreateUpdatePayInvoiceModel>
    ) => {
      setAPIRequestPending(false);
      if (response.result.success) {
        const succesMessage = data.isPayPopUp
          ? "Invoice paid successfully"
          : data.id
            ? "Invoice updated successfully"
            : "Invoice saved successfully";
        ToastService.showSuccess(succesMessage);
        onClose(true, response.result.contactId);
      } else {
        setAPIRequestPending(false);
        ToastService.showError(errorMessage);
      }
    };
    const errorResponse = (error: any) => {
      console.error(error);
      setAPIRequestPending(false);
      ToastService.showError(errorMessage);
    };
    if (invoiceData.isPayPopUp) {
      const payRequest = data.createPayInvoiceModel();
      InvoiceService.payInvoice(payRequest).subscribe({
        next: successResposne,
        error: errorResponse,
      });
    } else {
      if (data.id) {
        const udpateRequest = data.createUpdateInvoiceModel(
          data.contact?.id || ""
        );
        InvoiceService.updateInvoice(udpateRequest).subscribe({
          next: successResposne,
          error: errorResponse,
        });
      } else {
        const mindmeContactId = AuthService.isMindMeContactRoute
          ? AuthService.getMindMeContactId
          : data.contact?.mindmeContactId || "";
        const request = data.createNewInvoiceAPIModel(
          data.contact?.id || "",
          mindmeContactId
        );
        InvoiceService.saveInvoice(request).subscribe({
          next: successResposne,
          error: errorResponse,
        });
      }
    }
  }

  function singleSelect(
    selectedKey: string,
    fieldName: keyof UpsertInvoiceModel,
    options: IOption[],
    hasError: boolean,
    className?: string,
    searchable?: boolean
  ) {
    return (
      <SingleSelect
        searchable={searchable === true}
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={(option) => {
          updateState(fieldName, option.value);
        }}
        className={className}
      />
    );
  }
  function statusDropDown() {
    const colors = InvoiceUtil.getColorsByStatus(data.status);
    if (data.isPayPopUp) {
      const colors = InvoiceUtil.getColorsByStatus(data.status);
      return row(
        "Status",
        <CustomButton
          bgColor={colors.bgColor}
          color={colors.color}
          className="status-btn"
        >
          {InvoiceStatus[data.status]}
        </CustomButton>
      );
    }
    if (!isUpdate()) {
      return null;
    }

    const options = Object.keys(InvoiceStatus).map((key) => {
      return {
        label: InvoiceStatus[key as keyof typeof InvoiceStatus],
        value: key,
      };
    });

    return row(
      "Status",
      <StatusDropdownWrapper bgColor={colors.bgColor} color={colors.color}>
        {singleSelect(data.status, "status", options, false, "status-dropdown")}
      </StatusDropdownWrapper>
    );
  }

  function amountRow() {
    if (isUpdate() || invoiceData.isPayPopUp) {
      return row(
        "Amount",
        <CustomLabel lineHeight="40px">
          ${AccountSettingsStore.formatAmount(parseFloat(data.amount), true)}
        </CustomLabel>
      );
    }

    return (
      <>
        {row(
          "Amount Due",
          <CustomLabel lineHeight="40px">
            {textInput("text", "amount", data.amount, "Enter amount", "")}
          </CustomLabel>
        )}
      </>
    );
  }

  function paymentSection() {
    if (
      !isUpdate() ||
      data.isPayPopUp ||
      data.payments.length === 0 ||
      !showBalanceDetails
    )
      return null;

    return (
      <>
        {row(
          "Sub-Total",
          <CustomLabel fontWeight={800}>
            $
            {AccountSettingsStore.formatAmount(
              parseFloat(data.amount) - data.totalCreditAmount,
              true
            )}
          </CustomLabel>
        )}
        {row(
          "Payments",
          <>
            <FlexContainer
              fxLayout="column"
              spacing={10}
              className="payment-section-wrapper"
            >
              {data.payments.map((p, idx) => {
                const formattedAmount = `$${AccountSettingsStore.formatAmount(p.amount, true)}`;
                return (
                  <div key={p.id}>
                    <FlexContainer spacing={50} className="payment-section">
                      <CustomLabel
                        fontSize={"16px"}
                        color={p.isRefund ? "" : redColor}
                      >
                        {p.isRefund ? formattedAmount : `(${formattedAmount})`}
                      </CustomLabel>
                      <CustomLabel fontSize={"16px"}>
                        {PaymentTransactionStatus[p.status]}
                      </CustomLabel>
                      <CustomLabel fontSize={"16px"}>
                        {UtilityMethods.formatDateInString(p.createdDate)}
                      </CustomLabel>
                      <RenderPaymentMethod paymentMethod={p.paymentMethod} />
                    </FlexContainer>
                    {data.payments.length === idx + 1 && <hr />}
                  </div>
                );
              })}
            </FlexContainer>
          </>
        )}
      </>
    );
  }

  function amountAndPaymentDetails() {
    if (!isUpdate() && !invoiceData.isPayPopUp) {
      return amountRow();
    }

    if (data.isPayPopUp && !showPayBalanceDetails) {
      return null;
    }

    if (!data.isPayPopUp && isUpdate() && !showBalanceDetails) return null;

    return (
      <>
        {amountRow()}
        {row(
          "Discount",
          <CustomLabel color={redColor} lineHeight="40px">
            (${AccountSettingsStore.formatAmount(data.discount, true)})
          </CustomLabel>
        )}

        {row(
          "Credit",
          <CustomLabel color={redColor} lineHeight="40px">
            (${AccountSettingsStore.formatAmount(data.totalCreditAmount, true)})
          </CustomLabel>
        )}

        {data.isPayPopUp &&
          row(
            "Surcharge",
            <CustomLabel lineHeight="40px">
              {" "}
              ${AccountSettingsStore.formatAmount(data.surcharge, true)}{" "}
            </CustomLabel>,
            undefined,
            true
          )}

        {paymentSection()}

        {/* {row(
                    'Paid',
                    <CustomLabel color={redColor}>(${UtilityMethods.numberWithCommas(data.paidAmount)})</CustomLabel>,
                )} */}
        {/* {row(
                    'Refund',
                    <CustomLabel color={redColor}>
                        (${UtilityMethods.numberWithCommas(data.totalRefundedAmount)})
                    </CustomLabel>,
                )} */}

        {/* {(data.status === InvoiceStatus.Paid || invoiceData.isPayPopUp) &&
                    row(
                        'Balance',
                        <CustomLabel>
                            ${UtilityMethods.numberWithCommas(data.balance)} on{' '}
                            {UtilityMethods.formatDateInString(new Date(data.dueDate))}
                        </CustomLabel>,
                    )} */}
        {/* {!invoiceData.isPayPopUp &&
                    data.status !== InvoiceStatus.Paid &&
                    row(
                        'Balance Due',
                        <CustomLabel>
                            {textInput('text', 'balanceValue', data.balanceValue, 'Enter balance', '')}
                        </CustomLabel>,
                    )} */}
      </>
    );
  }

  function payRow() {
    if (!invoiceData.isPayPopUp) {
      return null;
    }

    return row(
      "Pay",
      <CustomLabel>
        {textInput(
          "text",
          "amountToPay",
          data.amountToPay,
          "Enter amount to pay",
          ""
        )}
      </CustomLabel>
    );
  }

  function onNewPaymentMethod(isNewPaymentMethod: boolean, token: string) {
    setInvoiceData(
      (prevData) =>
        new UpsertInvoiceModel({
          ...prevData,
          isNewPaymentMethod: isNewPaymentMethod,
          newPaymentGatewayMethodId: token,
        })
    );
  }

  function onContactChange(contact: ContactReferenceModel) {
    const defaultPaymentMethod =
      PaymentMethodModel.getDefaultSelecedPaymentMethod(
        paymentMethods,
        contact.id
      );
    setInvoiceData(
      (prevData) =>
        new UpsertInvoiceModel({
          ...prevData,
          contact: contact,
          paymentMethod: defaultPaymentMethod,
          isNewPaymentMethodPrimary: !defaultPaymentMethod ? true : false,
        })
    );
  }

  return (
    <ModalComponent
      title={
        data.isPayPopUp
          ? "Pay Invoice"
          : isUpdate()
            ? "Update Invoice"
            : "New Invoice"
      }
      titleFontSize="22px"
      bodyPadding="40px 50px 10px 50px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="900px"
      bodyBackgroundColor="#fff"
      footerBackground="#F4F7FB"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <SapphireBlueButton
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Cancel
          </SapphireBlueButton>
          {invoiceData.isPayPopUp && (
            <SapphireBlueButton
              disabled={isAPIRequestPending}
              minWidth="120px"
              height="30px"
              onClick={() => onSubmit()}
            >
              Pay
            </SapphireBlueButton>
          )}

          {!invoiceData.isPayPopUp && (
            <SapphireBlueButton
              disabled={isAPIRequestPending}
              primary
              minWidth="120px"
              height="30px"
              onClick={() => onSubmit()}
            >
              {isUpdate() ? "Update" : "Save"}
            </SapphireBlueButton>
          )}
        </FlexContainer>
      }
    >
      {showLoader ? (
        new Loader(true).loaderComponent
      ) : (
        <InvoicePopupWrapper className="subscription-popup">
          {!AuthService.isMindMeContactRoute && (
            <>
              {(isUpdate() || data.isPayPopUp) &&
                data.contact &&
                row(
                  "Customer",
                  <RenderContactInfo contact={data.contact} />,
                  "invoice-description-block"
                )}

              {(!data.contact || (!isUpdate() && !data.isPayPopUp)) &&
                row(
                  "Customer",
                  <ContactDropDown
                    selectedContact={data.contact}
                    contacts={contacts}
                    hasError={data.hasError("contact")}
                    onSelect={(selectedContact) => {
                      onContactChange(selectedContact);
                    }}
                    hasMoreContacts={hasMoreContacts}
                    onRefresh={(
                      contactId,
                      mindmeContactId,
                      searchValue,
                      pageSize
                    ) => {
                      fetchContacts(
                        contactId,
                        mindmeContactId,
                        searchValue,
                        pageSize
                      );
                    }}
                    className="white-bg"
                  />
                )}
            </>
          )}

          {isUpdate() &&
            row(
              "Invoice No",
              <CustomLabel lineHeight="40px">{data.invoiceNumber}</CustomLabel>,
              "invoice-description-block"
            )}

          {statusDropDown()}
          {row(
            "Description",
            <CustomLabel>
              {data.isPayPopUp
                ? data.description
                : textInput(
                    "textarea",
                    "description",
                    data.description,
                    "Enter Description",
                    ""
                  )}
            </CustomLabel>,
            "invoice-description-block"
          )}

          {amountAndPaymentDetails()}
          {(isUpdate() || data.isPayPopUp) &&
            row(
              "Balance",
              <CustomLabel fontWeight={600} lineHeight="40px" color="#000">
                <FlexContainer spacing={10} alignItems="center">
                  ${AccountSettingsStore.formatAmount(data.balance, true)}{" "}
                  &nbsp;
                  {data.payments.length > 0 && (
                    <CustomLabel
                      color="#556BB8"
                      className="clickable"
                      onClick={() =>
                        data.isPayPopUp
                          ? togglePayBalanceDetails(!showPayBalanceDetails)
                          : toggleBalanceDetails(!showBalanceDetails)
                      }
                    >
                      {showBalanceDetails || showPayBalanceDetails
                        ? "hide details"
                        : "show details"}
                    </CustomLabel>
                  )}
                </FlexContainer>
              </CustomLabel>
            )}
          {payRow()}
          {!data.isPayPopUp &&
            row(
              "Payment Due",
              <div>
                <FlexContainer fxLayout="column" spacing={5}>
                  <FlexContainer
                    fxLayout="row"
                    spacing={15}
                    alignItems="center"
                  >
                    {textInput(
                      "date",
                      "dueDate",
                      UtilityMethods.toLocalISOString(new Date(data.dueDate)),
                      "Enter due date",
                      "invoice-payment-datepicker"
                    )}
                    {/* <TimeComponent
                    hhmm={data.time}
                    error={data.hasError("time")}
                    amPm={data.amPm}
                    onChange={(amPm: keyof typeof TimeAMPM, time: string) =>
                      updateTime(amPm, time)
                    }
                    className="invoice-timezone"
                  /> */}
                    <TextInputWithDropdown
                      textInputValue={data.time}
                      dropdownValue={data.amPm}
                      onTextChange={(value) => updateTime(data.amPm, value)}
                      onDropdownChange={(option) =>
                        updateTime(option.value, data.time)
                      }
                      className="invoice-timezone subscription-timezone"
                      dropdownOptions={[
                        Object.keys(TimeAMPM).map((key) => ({
                          value: key,
                          label: TimeAMPM[key as keyof typeof TimeAMPM],
                        })),
                      ]}
                      error={data.hasError("time")}
                      placeholderForTextInput="hh:mm"
                      isTime={true}
                    />
                    <FlexContainer alignItems="center">
                      <CustomLabel>
                        {AccountSettingsStore.generalSettings.timeZone
                          .abbreviation || ""}
                      </CustomLabel>
                    </FlexContainer>
                  </FlexContainer>
                  {isUpdate() && (
                    <CustomLabel fontSize="12px" lineHeight="12px">
                      Changing to a future date will make this Invoice current.
                    </CustomLabel>
                  )}
                </FlexContainer>
              </div>,
              "invoice-payment-due"
            )}
          {!isUpdate() &&
            row(
              "Save as Draft",
              <CustomLabel lineHeight="40px" className="save-draft-chkbx">
                {checkBox(
                  "No payment will be processed",
                  data.isDraft,
                  "isDraft"
                )}
              </CustomLabel>
            )}

          {merchantAccounts.length > 1 &&
            row(
              "Merchant Account",
              <MerchantAccountsDropDown
                selectedMerchantAccount={data.merchantAccount}
                merchantAccounts={merchantAccounts}
                showError={data.hasError("merchantAccount")}
                onChange={(option) => updateState("merchantAccount", option)}
                className="white-bg"
              />
            )}
          {row(
            "Billing Method",
            <BillingMethods
              selectedMethod={data.billingMethod}
              onChange={(value) => updateState("billingMethod", value)}
            />,
            "billing-method-lh"
          )}
          {row(
            "Payment Method",
            <PaymentMethodDropdown
              merchantAccount={data.merchantAccount}
              contactId={data.contact?.id || ""}
              isRenderWithoutContact={
                (!invoiceData.contact?.id &&
                  AuthService.isMindMeContactRoute) ||
                data.contact?.mindmeContactId
                  ? true
                  : false
              }
              selectedPaymentMethod={data.paymentMethod}
              paymentMethods={paymentMethods}
              onChange={(option) => updateState("paymentMethod", option)}
              hasError={data.hasError("paymentMethod")}
              className="invoice-payment-method"
              hasErrorInNewPaymentMethod={data.hasError(
                "newPaymentGatewayMethodId"
              )}
              onNewPaymentMethod={(
                isNewPaymentMethod,
                newPaymentGatewayMethodId
              ) => {
                onNewPaymentMethod(
                  isNewPaymentMethod,
                  newPaymentGatewayMethodId
                );
              }}
              headerClass="subscription-payment-method"
              updateNewMethodAsPrimary={(isNewPaymentMethodPrimary) =>
                updateState(
                  "isNewPaymentMethodPrimary",
                  isNewPaymentMethodPrimary
                )
              }
              isLodingInProcess={showLoader}
              isNewPaymentMethodPrimary={data.isNewPaymentMethodPrimary}
            />
          )}
          {data.isPayPopUp &&
            row(
              "",
              <CustomLabel margin="0px" fontSize={"14px"} color={redColor}>
                Invoice cannot be modified once paid in full.
              </CustomLabel>,
              "pay-popup-warning"
            )}
        </InvoicePopupWrapper>
      )}
    </ModalComponent>
  );
};

export default withRouter(observer(UpsertInvoicePopUp));
